/**
 * Utility function to get the local IP address using WebRTC
 * This captures the user's local network IP address
 */
export const getLocalIP = () => {
  return new Promise((resolve) => {
    // Create a temporary RTCPeerConnection
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    });

    // Create a data channel to trigger ICE gathering
    pc.createDataChannel('');

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        const candidate = event.candidate.candidate;
        // Extract IP address from ICE candidate
        const ipMatch = candidate.match(/([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-f0-9]{1,4}(:[a-f0-9]{1,4}){7})/);
        if (ipMatch && ipMatch[1]) {
          const ip = ipMatch[1];
          // Filter out public IPs and localhost, keep only local network IPs
          if (!ip.startsWith('127.') && !ip.startsWith('::1') && !ip.startsWith('0.0.0.0')) {
            pc.close();
            resolve(ip);
            return;
          }
        }
      }
    };

    // Create offer to start ICE gathering
    pc.createOffer()
      .then(offer => pc.setLocalDescription(offer))
      .catch(() => {
        pc.close();
        resolve(null);
      });

    // Timeout after 3 seconds
    setTimeout(() => {
      pc.close();
      resolve(null);
    }, 3000);
  });
};

/**
 * Fallback method to get local IP using a simple fetch to a local service
 * This is less reliable but works in some cases
 */
export const getLocalIPFallback = async () => {
  try {
    // Try to get IP from a local service (this won't work in most cases)
    const response = await fetch('/api/local-ip', { timeout: 1000 });
    if (response.ok) {
      const data = await response.json();
      return data.ip;
    }
  } catch (error) {
    // Ignore errors
  }
  return null;
};

/**
 * Main function to get local IP with fallback
 */
export const getLocalIPAddress = async () => {
  try {
    // Try WebRTC method first
    const ip = await getLocalIP();
    if (ip) {
      return ip;
    }
    
    // Try fallback method
    return await getLocalIPFallback();
  } catch (error) {
    console.warn('Could not get local IP address:', error);
    return null;
  }
};
